import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractGoodsInwardsPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { goodsInwards: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      goodsInwards: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.goodsInwards ||
    dataBlock.goodsInward ||
    dataBlock.items ||
    root.goodsInwards;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list =
        inner.goodsInwards || inner.goodsInward || inner.items || undefined;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.goodsInwards == null &&
    (dataBlock._id != null || dataBlock.giNumber != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    goodsInwards: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateTimeMatch = str.match(/^(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]} ${dateTimeMatch[2]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function statusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "badge-success";
  if (normalized === "draft") return "badge-secondary";
  return "badge-info";
}

function renderRecordStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalizeStatus(status);

  if (normalized === "inactive") {
    return (
      <span
        className="fwt_600 f_red"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  if (normalized === "active") {
    return (
      <span
        className="fwt_600 f_green"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`badge ${statusBadgeClass(status)}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {label}
    </span>
  );
}

function personName(value) {
  if (!value) return "-";
  if (typeof value === "string") return value.trim() || "-";
  return value.name || value.userName || "-";
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayQty(value) {
  if (value == null || value === "") return "-";
  return value;
}

/** Material fields live on materials[]; fall back to flat legacy fields. */
function getGiMaterials(row) {
  if (Array.isArray(row?.materials) && row.materials.length) {
    return row.materials;
  }
  return [];
}

function sumMaterialField(materials, key) {
  let total = 0;
  let found = false;
  materials.forEach((m) => {
    if (m?.[key] == null || m[key] === "") return;
    const n = Number(m[key]);
    if (!Number.isFinite(n)) return;
    total += n;
    found = true;
  });
  return found ? total : null;
}

function getGiMaterialSummary(row) {
  const materials = getGiMaterials(row);
  if (!materials.length) {
    return {
      materialCode: row?.materialCode,
      poQuantity: row?.poQuantity,
      acceptedQuantity: row?.acceptedQuantity,
      availableStock: row?.availableStock,
      poBalanceQuantity: row?.poBalanceQuantity,
    };
  }

  const codes = materials
    .map((m) => String(m?.materialCode || "").trim())
    .filter((c) => c && c !== "-");

  let materialCode = row?.materialCode;
  if (codes.length === 1) materialCode = codes[0];
  else if (codes.length > 1)
    materialCode = `${codes[0]} (+${codes.length - 1})`;
  else if (materials[0]?.materialCode) materialCode = materials[0].materialCode;

  return {
    materialCode,
    poQuantity: sumMaterialField(materials, "poQuantity") ?? row?.poQuantity,
    acceptedQuantity:
      sumMaterialField(materials, "acceptedQuantity") ?? row?.acceptedQuantity,
    availableStock:
      sumMaterialField(materials, "availableStock") ?? row?.availableStock,
    poBalanceQuantity:
      sumMaterialField(materials, "poBalanceQuantity") ??
      row?.poBalanceQuantity,
  };
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  // { value: "inactive", label: "Inactive" },
];

function extractGiSuppliers(responseData) {
  const data = responseData?.data;
  const list =
    (Array.isArray(data?.suppliers) && data.suppliers) ||
    (Array.isArray(data) && data) ||
    (Array.isArray(responseData?.suppliers) && responseData.suppliers) ||
    [];
  return list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      return String(item?.supplierName || item?.supplier || "").trim();
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function extractGiUsers(responseData) {
  const data = responseData?.data;
  const list =
    (Array.isArray(data?.users) && data.users) ||
    (Array.isArray(data) && data) ||
    (Array.isArray(responseData?.users) && responseData.users) ||
    [];
  return list
    .map((user) => ({
      _id: String(user?._id || user?.id || "").trim(),
      name: String(user?.name || user?.userName || user?.email || "").trim(),
      email: String(user?.email || "").trim(),
    }))
    .filter((user) => user._id && user.name);
}

const GI_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total GI",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
  },
  {
    key: "activeCount",
    label: "Active",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "active",
  },
  {
    key: "draftCount",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterValue: "draft",
  },
  // {
  //   key: "inactiveCount",
  //   label: "Inactive",
  //   titleColor: "#922B21",
  //   valueColor: "#E74C3C",
  //   filterValue: "inactive",
  // },
];

const GiStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="card-body">
        <div
          className="stat-title"
          style={{
            color: titleColor,
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {label}
        </div>
        <div
          className="stat-value"
          style={{
            color: valueColor,
            fontSize: "20px",
            fontWeight: "700",
          }}
        >
          {value ?? 0}
        </div>
      </div>
    </div>
  </div>
);

const GiStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-3 col-sm-6 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

const GI_SORT_FIELDS = [
  "giNumber",
  "giDate",
  "materialCode",
  "poNumber",
  "supplierName",
  "createdAt",
  "updatedAt",
];

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";

function normalizeSortBy(value) {
  const field = String(value || "").trim();
  return GI_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY;
}

function normalizeSortOrder(value) {
  const order = String(value || "")
    .trim()
    .toLowerCase();
  return order === "asc" ? "asc" : "desc";
}

const DEFAULT_FILTER_CHECKS = {
  status: false,
  supplierName: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  status: "",
  supplierName: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  status: "",
  supplierName: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const MAX_ACTIVE_FILTERS = 2; 
const GoodsInwards = () => {
  const { canCreate, canView, canUpdate ,canDelete} = useModuleAccess("goods_inwards");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("goodsInwardsFilters") || "{}",
  );

  const currentUserRole = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = currentUserRole === "admin";

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [activeMultiFilters, setActiveMultiFilters] = useState(
    savedFilters.activeMultiFilters || DEFAULT_ACTIVE_MULTI_FILTERS,
  );
  const filterPanelRef = useRef(null);

  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY),
  );
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER),
  );
  const [giCounts, setGiCounts] = useState({
    totalCount: 0,
    activeCount: 0,
    // inactiveCount: 0,
    draftCount: 0,
  });
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [giSuppliers, setGiSuppliers] = useState([]);
  const [giUsers, setGiUsers] = useState([]);
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const loadRequestIdRef = useRef(0);
  const [deleteTarget, setDeleteTarget] = useState(null); 
  const [isDeleting, setIsDeleting] = useState(false);

  const anyMultiFilterActive =
    !!activeMultiFilters.status ||
    !!activeMultiFilters.supplierName ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

 
  const buildMultiFilterFields = useCallback(
    (fields = {}, { includeStatus = true } = {}) => {
      if (activeMultiFilters.supplierName) {
        fields.supplierName = String(activeMultiFilters.supplierName).trim();
      }
      if (activeMultiFilters.createdBy) {
        fields.createdBy = String(activeMultiFilters.createdBy).trim();
      }
      if (includeStatus && activeMultiFilters.status) {
        fields.status = String(activeMultiFilters.status).toLowerCase();
      }
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  const fetchGoodsInwardsCounts = useCallback(async () => {
    setIsCountsLoading(true);
    try {
      const payload = { action: "goods-inwards-counts" };
  
      buildMultiFilterFields(payload, { includeStatus: false });

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData) && responseData.data) {
        const data = responseData.data;
        setGiCounts({
          totalCount: Number(data.totalCount) || 0,
          activeCount: Number(data.activeCount) || 0,
          // inactiveCount: Number(data.inactiveCount) || 0,
          draftCount: Number(data.draftCount) || 0,
        });
      }
    } catch (error) {
      console.error("Goods inwards counts error:", error);
    } finally {
      setIsCountsLoading(false);
    }
  }, [buildMultiFilterFields]);

  const fetchGoodsInwards = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const payload = {
          action: "get-all-goods-inwards",
          page: currentPage,
          limit: itemsPerPage,
          sortBy: normalizeSortBy(sortBy),
          sortOrder: normalizeSortOrder(sortOrder),
        };

        const trimmed = String(activeSearchQuery || "").trim();
        if (trimmed) {
          payload.search = trimmed;
        }

        buildMultiFilterFields(payload);
        if (payload.createdBy) {
          payload.createduserid = payload.createdBy;
          delete payload.createdBy;
        }

        const response = await axios.post(API_URL, payload);
        if (requestId !== loadRequestIdRef.current) return;

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        const { goodsInwards, pagination } =
          extractGoodsInwardsPayload(responseData);

        if (isApiSuccess(responseData) || goodsInwards.length > 0) {
          setRows(goodsInwards);
          const total =
            typeof pagination.total === "number"
              ? pagination.total
              : typeof pagination.totalCount === "number"
                ? pagination.totalCount
                : goodsInwards.length;
          setTotalCount(total);
        } else {
          setRows([]);
          setTotalCount(0);
          if (responseData?.message) {
            toast.error(responseData.message, { autoClose: 2000 });
          }
        }
      } catch (error) {
        console.error("Fetch goods inwards error:", error);
        if (requestId !== loadRequestIdRef.current) return;
        setRows([]);
        setTotalCount(0);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(errData?.message || "Failed to fetch goods inwards", {
          autoClose: 2000,
        });
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      currentPage,
      itemsPerPage,
      activeSearchQuery,
      buildMultiFilterFields,
      sortBy,
      sortOrder,
    ],
  );

  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    fetchGoodsInwards(requestId);
  }, [fetchGoodsInwards]);

  useEffect(() => {
    fetchGoodsInwardsCounts();
  }, [fetchGoodsInwardsCounts]);

  useEffect(() => {
    let cancelled = false;

    const fetchFilterOptions = async () => {
      setIsFilterOptionsLoading(true);
      try {
        const [suppliersRes, usersRes] = await Promise.all([
          axios.post(API_URL, { action: "get-goods-inwards-suppliers" }),
          axios.post(API_URL, { action: "get-goods-inwards-users" }),
        ]);
        if (cancelled) return;

        const suppliersData = normalizeApiPayload(
          parseApiResponse(suppliersRes.data),
        );
        const usersData = normalizeApiPayload(parseApiResponse(usersRes.data));

        if (isApiSuccess(suppliersData)) {
          setGiSuppliers(extractGiSuppliers(suppliersData));
        }
        if (isApiSuccess(usersData)) {
          setGiUsers(extractGiUsers(usersData));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch GI filter options error:", error);
        }
      } finally {
        if (!cancelled) setIsFilterOptionsLoading(false);
      }
    };

    fetchFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "goodsInwardsFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        filterChecks,
        filterInputs,
        activeMultiFilters,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = rows.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);
  const hasActiveFilters = Boolean(activeSearchQuery || anyMultiFilterActive);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      start = 2;
      end = 5;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
      end = totalPages - 1;
    }

    if (start > 2) pages.push("ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };


  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) setCurrentPage(1);
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

    const openDeleteConfirm = (row) => {
    const id = row._id || row.id;
    setDeleteTarget({ id, giNumber: row.giNumber || "" });
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-goods-inwards",
        id: deleteTarget.id,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(responseData.message || "Goods inwards deleted successfully", {
          autoClose: 2000,
        });
        setDeleteTarget(null);
        const requestId = ++loadRequestIdRef.current;
        fetchGoodsInwards(requestId);
        fetchGoodsInwardsCounts();
      } else {
        toast.error(responseData?.message || "Failed to delete goods inwards", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete goods inwards error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to delete goods inwards", {
        autoClose: 2000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isGiStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.status;
    }
    return String(activeMultiFilters.status).toLowerCase() === card.filterValue;
  };

  const handleGiStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterChecks((prev) => ({ ...prev, status: false }));
      setFilterInputs((prev) => ({ ...prev, status: "" }));
      setActiveMultiFilters((prev) => ({ ...prev, status: "" }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    if (!filterChecks.status) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== "status" && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          status: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply Status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, status: true }));
        setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          status: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
      setActiveMultiFilters((prev) => ({ ...prev, status: card.filterValue }));
    }

    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    setActiveSearchQuery(trimmedQuery);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleSort = (column) => {
    const apiField = normalizeSortBy(column);

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    if (currentPage !== 1) setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = normalizeSortBy(column);

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Goods Inwards</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/goodsInwardsCreate" className="btn btn-blue">
                  Create Goods Inwards
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isCountsLoading
                  ? GI_STAT_CARDS.map((card) => (
                      <GiStatCardSkeleton key={card.key} />
                    ))
                  : GI_STAT_CARDS.map((card) => (
                      <GiStatCard
                        key={card.key}
                        label={card.label}
                        value={giCounts[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isGiStatCardActive(card)}
                        onClick={() => handleGiStatCardClick(card)}
                      />
                    ))}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search GI No, Material, PO, Supplier, Invoice..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                              title="Search"
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Filter
                        </label>
                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>
                          <i
                            className={`fa-solid fa-chevron-${isFilterPanelOpen ? "up" : "down"}`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          ></i>
                        </button>
                        {isFilterPanelOpen && (
                          <div
                            className="gi-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckStatus"
                                checked={filterChecks.status}
                                disabled={
                                  !filterChecks.status &&
                                  Object.values(filterChecks).filter(Boolean)
                                    .length >= MAX_ACTIVE_FILTERS
                                }
                                onChange={() =>
                                  handleFilterCheckToggle("status")
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckStatus"
                              >
                                Status
                              </label>
                            </div>
                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckSupplier"
                                checked={filterChecks.supplierName}
                                disabled={
                                  !filterChecks.supplierName &&
                                  Object.values(filterChecks).filter(Boolean)
                                    .length >= MAX_ACTIVE_FILTERS
                                }
                                onChange={() =>
                                  handleFilterCheckToggle("supplierName")
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckSupplier"
                              >
                                Supplier
                              </label>
                            </div>
                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckCreatedBy"
                                checked={filterChecks.createdBy}
                                disabled={
                                  !filterChecks.createdBy &&
                                  Object.values(filterChecks).filter(Boolean)
                                    .length >= MAX_ACTIVE_FILTERS
                                }
                                onChange={() =>
                                  handleFilterCheckToggle("createdBy")
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckCreatedBy"
                              >
                                Created User
                              </label>
                            </div>
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckDateRange"
                                checked={filterChecks.dateRange}
                                disabled={
                                  !filterChecks.dateRange &&
                                  Object.values(filterChecks).filter(Boolean)
                                    .length >= MAX_ACTIVE_FILTERS
                                }
                                onChange={() =>
                                  handleFilterCheckToggle("dateRange")
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckDateRange"
                              >
                                Date
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status dropdown - shown only when its checkbox is checked */}
                    {filterChecks.status && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Status
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.status}
                            onChange={(e) =>
                              handleFilterInputChange("status", e.target.value)
                            }
                          >
                            <option value="">All</option>
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.supplierName && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Supplier
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.supplierName}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "supplierName",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isFilterOptionsLoading &&
                              giSuppliers.length === 0
                                ? "Loading suppliers..."
                                : "All suppliers"}
                            </option>
                            {giSuppliers.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Created User
                          </label>
                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isFilterOptionsLoading && giUsers.length === 0
                                ? "Loading users..."
                                : "All users"}
                            </option>
                            {giUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                                {user.email ? ` (${user.email})` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.fromDate || ""}
                              max={filterInputs.toDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "fromDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.toDate || ""}
                              min={filterInputs.fromDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "toDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {hasActiveFilters && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading && rows.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : rows.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no goods inwards yet.
                  <br />
                  <div className="mt-3">
                    {canCreate && (
                      <Link to="/goodsInwardsCreate" className="btn btn-blue">
                        Create Goods Inwards
                      </Link>
                    )}
                  </div>
                </div>
              ) : rows.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No goods inwards found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="goods-inwards"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("giNumber", "GI Number", {
                            minWidth: "110px",
                          })}
                          {renderSortableHeader("giDate", "GI Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader(
                            "materialCode",
                            "Material Code",
                            { minWidth: "130px" },
                          )}
                          {renderSortableHeader("poNumber", "PO Number", {
                            minWidth: "110px",
                          })}
                          <th style={{ minWidth: "90px" }}>PO Qty</th>
                          <th
                            style={{ minWidth: "110px", whiteSpace: "nowrap" }}
                          >
                            Accepted Qty
                          </th>
                          <th
                            style={{ minWidth: "120px", whiteSpace: "nowrap" }}
                          >
                            Available Stock
                          </th>
                          <th
                            style={{ minWidth: "120px", whiteSpace: "nowrap" }}
                          >
                            PO Balance Qty
                          </th>
                          {renderSortableHeader("supplierName", "Supplier", {
                            minWidth: "140px",
                          })}
                          <th style={{ minWidth: "90px" }}>Status</th>
                          <th style={{ minWidth: "130px" }}>Created By</th>
                          <th
                            style={{
                              width: "70px",
                              minWidth: "70px",
                              textAlign: "center",
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => {
                          const id = row._id || row.id;
                          const material = getGiMaterialSummary(row);
                          return (
                            <tr key={id || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayValue(row.giNumber)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDate(row.giDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayValue(material.materialCode)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayValue(row.poNumber)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayQty(material.poQuantity)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayQty(material.acceptedQuantity)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayQty(material.availableStock)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {displayQty(material.poBalanceQuantity)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "160px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {displayValue(row.supplierName)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderRecordStatus(row.status)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {personName(row.createdBy)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <Link
                                      to={`/goodsInwardsView/${id}`}
                                      className="crsr_pntr"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {canUpdate &&
                                    String(row.status || "").toLowerCase() ===
                                      "draft" && (
                                      <Link
                                        to={`/goodsInwardsEdit/${id}`}
                                        className="crsr_pntr ml-2"
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pen" />
                                      </Link>
                                    )}
                                      {canDelete && isAdmin && String(row.status || "").toLowerCase() ===
                                      "active" &&  (
                                    
                                      <span
                                        className="crsr_pntr ml-2"
                                        title="Delete"
                                        style={{
                                      color: "var(--blue, #0094a9)",
                                      textDecoration: "none",
                                    }}
                                        onClick={() => openDeleteConfirm(row)}
                                      >
                                        <i
                                      className="fa-solid fa-trash-can"
                                      style={{ color: "inherit" }}
                                      aria-hidden="true"
                                    />
                                      </span>
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {rows.length > 0 && (
                    <div
                      className="supply-pagination-container"
                      style={{
                        opacity: isLoading ? 0.65 : 1,
                        pointerEvents: isLoading ? "none" : "auto",
                      }}
                    >
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
       {deleteTarget && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeDeleteConfirm}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="close"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete{" "}
                <strong>{deleteTarget.giNumber || "this record"}</strong>?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsInwards;
